import React, { useEffect, useState } from 'react'

import { graphql, Link } from 'gatsby'
import { GatsbyImage } from 'gatsby-plugin-image'
import htmr from 'htmr'
import PropTypes from 'prop-types'
import {
  EmailIcon,
  EmailShareButton,
  FacebookIcon,
  FacebookShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  PinterestIcon,
  PinterestShareButton,
  RedditIcon,
  RedditShareButton,
  TumblrIcon,
  TumblrShareButton,
  TwitterIcon,
  TwitterShareButton,
  WhatsappIcon,
  WhatsappShareButton,
} from 'react-share'
import slugify from 'slugify'
import styled from 'styled-components'

import Layout from '../components/core/layout'
import SEO from '../components/core/seo'
import Card from '../components/elements/Card'
import Pill from '../components/elements/Pill'
import ComposedFormHireUMA from '../components/forms/ComposedFormHireUMA'
import LazyImage from '../components/reusables/LazyImage'
import BlogHero from '../components/specific-to-template/blog/BlogHero'
import HeroProps from '../components/specific-to-template/employers/industry-insights/HeroProps'
import { personas } from '../enums/persona'
import { usePersona } from '../hooks'
import { colors } from '../style/design-system'
import sp from '../utils/filters/shortcodeParser'
import { studentInfo } from '../utils/static-data/studentInfo'
import bls from '../utils/bls-citations'

const categoryString = (categories) => {
  if (categories.length > 0) {
    return categories.reduce((acc, cur) => {
      if (!acc) {
        return (
          <Pill>
            <Link to={`/employers/${slugify(cur.toLowerCase())}/`}>{cur}</Link>
          </Pill>
        )
      }
      return (
        <>
          {acc}
          <Pill>
            <Link to={`/employers/${slugify(cur.toLowerCase())}/`}>{cur}</Link>
          </Pill>
        </>
      )
    }, '')
  }
  return ''
}

const IndustryInsightsSingle = ({ className, data, pageContext, path }) => {
  const [siteURL, setSiteURL] = useState('')
  const [hasBlsCitation, setHasBlsCitation] = useState(false)

  useEffect(() => {
    setSiteURL(window.location.origin)
  }, [siteURL, setSiteURL])

  const { resetPersona, setPersona } = usePersona()
  useEffect(() => {
    setPersona(personas.EMPLOYER)
    return resetPersona
  }, [resetPersona, setPersona])

  const categories = categoryString(pageContext.categories)
  const pageURL = `${siteURL}${path}`
  const { author } = data
  const { content, featuredImage, metaPostTitle, metaSeoMetadesc, metaSeoTitle, modified } = data.page

  useEffect(() => {
    const hasBls = content && content?.indexOf('Bureau of Labor Statistics') !== -1
    setHasBlsCitation(hasBls)
  }, [])

  return (
    <Layout>
      <SEO
        title={metaSeoTitle || metaPostTitle}
        description={metaSeoMetadesc}
        ogImage={featuredImage?.node?.localFile?.publicURL}>
        <script type="application/ld+json">
          {`{
              "@context": "http://schema.org",
              "@type": "Article",
              "name": "${metaPostTitle}",
              "headline": "${metaPostTitle}",
              "dateModified": "${modified}",
              "image": "${
                featuredImage?.node?.localFile?.publicURL ? siteURL + featuredImage?.node?.localFile?.publicURL : ''
              }",
              "articleBody": "${content ? content.replace(/"/g, '\\"').replace(/\r?\n|\r/g, '') : ''}"
            }`}
        </script>
      </SEO>

      <BlogHero heroIsH1={false} Content={() => <HeroProps.Content />} title={HeroProps.title} />

      <div className={className}>
        <div className="container my-9 my-xl-0">
          <div className="row">
            <div className="col-md-8 col-xl-6 offset-xl-2">
              <main>
                <div className="blog-content">
                  <h1 className="mt-0 h2 blue">{htmr(sp(metaPostTitle))}</h1>
                  {categories && (
                    <p className="category-string mb-0">
                      {/* eslint-disable-next-line */}
                      <small>In: {categories}</small>
                    </p>
                  )}
                  {/* eslint-disable-next-line */}
                  {modified && (
                    <p>
                      <small>Updated: {modified}</small>
                      {author?.metaProfileName === 'Gretchen Adams' && (
                        <>
                          <br />
                          <small>Written by Ultimate Medical Academy</small>
                        </>
                      )}
                    </p>
                  )}
                  {featuredImage?.node?.localFile?.childImageSharp?.gatsbyImageData && (
                    <GatsbyImage
                      image={featuredImage.node.localFile.childImageSharp.gatsbyImageData}
                      className="mb-6"
                      loading="eager"
                      alt={metaPostTitle}
                    />
                  )}
                  <div className="page-content">
                    {content && htmr(sp(content))}
                    {hasBlsCitation && (
                      <p className="lh-1">
                        <small>{htmr(bls.global.usageDisclaimer)}</small>
                      </p>
                    )}
                    <hr />
                    {/* <p>
                      If you’re looking to fill your workforce with job-ready
                      healthcare graduates at no cost to your organization, connect
                      with our
                      <span> </span>
                      <Link to="/employers/our-team">Corporate Alliance Team</Link>
                      .
                    </p>
                    <p>
                      UMA’s Career Services team partners with healthcare organizations of varying
                      scales, providing allied healthcare staffing solutions at no cost. This
                      staffing team matches the growing needs of organizations with knowledgeable
                      graduates from Ultimate Medical Academy, a nonprofit healthcare educational
                      institution with a national presence for over
                      <span> </span>
                      {studentInfo.yearsInBusiness}
                      <span> </span>
                      years.
                    </p> */}
                  </div>
                </div>
                <div className="py-6">
                  <FacebookShareButton url={pageURL} className="social m-2">
                    <FacebookIcon className="social__icon" />
                  </FacebookShareButton>
                  <TwitterShareButton url={pageURL} className="social m-2">
                    <TwitterIcon className="social__icon" />
                  </TwitterShareButton>
                  <LinkedinShareButton url={pageURL} className="social m-2">
                    <LinkedinIcon className="social__icon" />
                  </LinkedinShareButton>
                  <PinterestShareButton url={pageURL} className="social m-2">
                    <PinterestIcon className="social__icon" />
                  </PinterestShareButton>
                  <WhatsappShareButton url={pageURL} className="social m-2">
                    <WhatsappIcon className="social__icon" />
                  </WhatsappShareButton>
                  <TumblrShareButton url={pageURL} className="social m-2">
                    <TumblrIcon className="social__icon" />
                  </TumblrShareButton>
                  <RedditShareButton url={pageURL} className="social m-2">
                    <RedditIcon className="social__icon" />
                  </RedditShareButton>
                  <EmailShareButton url={pageURL} className="social m-2">
                    <EmailIcon className="social__icon" />
                  </EmailShareButton>
                </div>
              </main>
            </div>

            <div className="col-md-4 col-xl-3 mt-md-11 mt-xl-0">
              <aside>
                {author && author?.metaProfileName !== 'Gretchen Adams' && (
                  <div className="about-the-author">
                    <h2 className="mt-0 mb-4 h3">About the Author</h2>
                    <Card className="p-4">
                      {author.metaProfilePic && (
                        <LazyImage
                          className="mb-4"
                          src={author.metaProfilePic}
                          alt={`${htmr(author.metaProfileName)} Profile Picture`}
                        />
                      )}
                      <p>{htmr(author.metaProfileShortBio)}</p>
                    </Card>
                  </div>
                )}
              </aside>
            </div>
          </div>
        </div>
        <div className="form-wrap">
          <div className="container container--sm">
            <ComposedFormHireUMA makeFullWidth />
          </div>
        </div>
      </div>
    </Layout>
  )
}

IndustryInsightsSingle.propTypes = {
  className: PropTypes.string,
  data: PropTypes.shape({
    page: PropTypes.shape({
      content: PropTypes.string,
      metaPostTitle: PropTypes.string,
      metaSeoMetadesc: PropTypes.string,
      metaSeoTitle: PropTypes.string,
      modified: PropTypes.string,
      uri: PropTypes.string,
      featuredImage: PropTypes.shape({
        node: PropTypes.shape({
          localFile: PropTypes.shape({
            publicURL: PropTypes.string,
            childImageSharp: PropTypes.shape({
              // eslint-disable-next-line
              gatsbyImageData: PropTypes.object,
            }),
          }),
        }),
      }),
    }),
    author: PropTypes.shape({
      id: PropTypes.string,
      metaProfileName: PropTypes.string,
      metaProfilePic: PropTypes.string,
      metaProfileShortBio: PropTypes.string,
    }),
  }),
  pageContext: PropTypes.shape({
    categories: PropTypes.arrayOf(PropTypes.string),
  }),
  path: PropTypes.string,
}

IndustryInsightsSingle.defaultProps = {
  className: undefined,
  data: undefined,
  pageContext: undefined,
  path: undefined,
}

export default styled(IndustryInsightsSingle)`
  .disclaimer {
    line-height: 1;
  }
  .social__icon {
    border-radius: 2px;
  }
  .form-wrap {
    background-color: ${colors.dev.gray.h()};
  }
  .about-the-author p {
    font-size: 1rem;
  }
`

export const query = graphql`
  query ($id: String!, $author: Int!) {
    page: wpPost(id: { eq: $id }) {
      content
      modified(formatString: "dddd, MMMM D, YYYY @ h:mma")
      metaPostTitle
      metaSeoTitle
      metaSeoMetadesc
      uri
      featuredImage {
        node {
          localFile {
            publicURL
            childImageSharp {
              gatsbyImageData(placeholder: NONE, layout: FULL_WIDTH)
            }
          }
        }
      }
    }
    author: wpUmaprofile(databaseId: { eq: $author }) {
      id
      metaProfileName
      metaProfilePic
      metaProfileShortBio
    }
  }
`
